// ─────────────────────────────────────────────────────────────────────────────
// LazyComponents.tsx - Code-split components using React.lazy()
// Forces webpack to create separate chunks for better loading performance
// ─────────────────────────────────────────────────────────────────────────────

import React, { Suspense } from 'react';

// Lazy load major page components
const Info = React.lazy(() => import('../pages/Info'));
const Patients = React.lazy(() => import('../pages/Patients'));
const PatientDetail = React.lazy(() => import('../Components/Core/PatientDetail'));
const SedationWorkflow = React.lazy(() => import('../pages/SedationWorkflow'));
const IntraSedation = React.lazy(() => import('../pages/IntraSedation'));
const PostSedation = React.lazy(() => import('../pages/PostSedation'));
const SedationList = React.lazy(() => import('../pages/SedationList'));

// Lazy load auth components
const Login = React.lazy(() => import('../Components/Auth/Login'));
const Register = React.lazy(() => import('../Components/Auth/Register'));

// Lazy load heavy components that might not be used immediately
const SyncDebugModal = React.lazy(() => import('../Components/Debug/SyncDebugModal'));
const DataProtectionReport = React.lazy(() => 
  import('../Components/Core/DataProtectionReport').then(module => ({
    default: module.DataProtectionReport
  }))
);
const ImportReport = React.lazy(() => import('../pages/ImportReport'));
const TipsAndTutorials = React.lazy(() => import('../pages/TipsAndTutorials'));

// Loading fallback component
const PageSkeleton: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    fontSize: '16px',
    color: '#666'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ marginBottom: '10px', fontSize: '24px' }}>⏳</div>
      <div>Loading...</div>
    </div>
  </div>
);

// HOC to wrap lazy components with Suspense
const withSuspense = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <Suspense fallback={fallback || <PageSkeleton />}>
      <Component {...props} />
    </Suspense>
  );
  
  WrappedComponent.displayName = `withSuspense(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Export wrapped components
export const LazyInfo = withSuspense(Info);
export const LazyPatients = withSuspense(Patients);
export const LazyPatientDetail = withSuspense(PatientDetail);
export const LazySedationWorkflow = withSuspense(SedationWorkflow);
export const LazyIntraSedation = withSuspense(IntraSedation);
export const LazyPostSedation = withSuspense(PostSedation);
export const LazySedationList = withSuspense(SedationList);
export const LazyLogin = withSuspense(Login);
export const LazyRegister = withSuspense(Register);
export const LazySyncDebugModal = withSuspense(SyncDebugModal);
export const LazyDataProtectionReport = withSuspense(DataProtectionReport);
export const LazyImportReport = withSuspense(ImportReport);
export const LazyTipsAndTutorials = withSuspense(TipsAndTutorials);

// Export the HOC for other components that need lazy loading
export { withSuspense };
export { PageSkeleton };
